import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import html2canvas from 'html2canvas';

// Helper to format date
const formatSeasonPeriod = (startStr: string, endStr: string) => {
    try {
        const start = new Date(startStr);
        const end = new Date(endStr);
        // French locale hardcoded as requested context implies FR primary
        const startFormatted = start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        const endFormatted = end.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        return startFormatted === endFormatted
            ? startFormatted.toUpperCase()
            : `${startFormatted} → ${endFormatted}`.toUpperCase();
    } catch {
        return "";
    }
};

// Short name helper (truncate)
const shortName = (name: string, max = 14) => {
    if (!name) return '';
    const clean = name.trim();
    const parts = clean.split(' ');
    if (clean.length > max && parts.length > 1) {
        return `${parts[0]} ${parts[parts.length - 1][0]}.`;
    }
    return clean;
};

// ELO Tier Logic
const ELO_TIERS = [
    { min: 0, max: 1149, name: { fr: "Débutant", en: "Beginner" }, level: 1 },
    { min: 1150, max: 1299, name: { fr: "Perfectionnement", en: "Improvement" }, level: 2 },
    { min: 1300, max: 1399, name: { fr: "Élémentaire", en: "Elementary" }, level: 3 },
    { min: 1400, max: 1499, name: { fr: "Intermédiaire", en: "Intermediate" }, level: 4 },
    { min: 1500, max: 1599, name: { fr: "Confirmé", en: "Confirmed" }, level: 5 },
    { min: 1600, max: 1749, name: { fr: "Avancé", en: "Advanced" }, level: 6 },
    { min: 1750, max: 1999, name: { fr: "Expert", en: "Expert" }, level: 7 },
    { min: 2000, max: Infinity, name: { fr: "Élite", en: "Elite" }, level: 8 }
];

export default function SeasonRecapPage() {
    const { clubId, seasonId, playerId } = useParams();
    const recapRef = useRef<HTMLDivElement>(null);
    const [isSharing, setIsSharing] = useState(false);

    const { data: stats, isLoading, error } = useQuery({
        queryKey: ['seasonStats', clubId, seasonId, playerId],
        queryFn: async () => {
            const res = await fetch(`/api/clubs/${clubId}/season-stats/${seasonId}/${playerId}`);
            if (!res.ok) throw new Error('Failed to fetch stats');
            return res.json();
        },
        enabled: !!clubId && !!seasonId && !!playerId
    });

    const handleShare = async () => {
        if (!recapRef.current || !stats) return;
        setIsSharing(true);

        try {
            // Generate canvas
            const canvas = await html2canvas(recapRef.current, {
                useCORS: true,
                scale: 1,
                backgroundColor: '#0a192f',
                width: 1080,
                height: 1920,
                onclone: (clonedDoc) => {
                    const container = clonedDoc.getElementById('social-share-capture');
                    if (container) {
                        container.style.transform = 'none';
                    }
                }
            });

            // Convert to blob
            canvas.toBlob(async (blob) => {
                if (!blob) {
                    setIsSharing(false);
                    return;
                }

                // Check for Web Share API support with files
                const nav = navigator as any;
                if (nav.share && nav.canShare && nav.canShare({ files: [new File([blob], 'recap.png', { type: 'image/png' })] })) {
                    try {
                        const file = new File([blob], `bilan-saison-${stats.clubName}.png`, { type: 'image/png' });
                        await nav.share({
                            title: `Mon Bilan de Saison - ${stats.clubName}`,
                            text: `Découvrez mon bilan PadELO pour la saison avec ${stats.clubName} !`,
                            files: [file]
                        });
                    } catch (err) {
                        console.error('Error sharing:', err);
                    }
                } else {
                    // Fallback: Download image
                    const link = document.createElement('a');
                    link.download = `bilan-saison-${stats.clubName}.png`;
                    link.href = canvas.toDataURL('image/png');
                    link.click();
                }
                setIsSharing(false);
            }, 'image/png');

        } catch (err) {
            console.error('Error generating image:', err);
            setIsSharing(false);
        }
    };

    if (isLoading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">Chargement...</div>;
    if (error) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-red-400">Erreur lors du chargement des statistiques de saison.</div>;
    if (!stats) return null;

    const clubElo = stats.clubEloRating || 1500;
    const currentTier = ELO_TIERS.find(tier => clubElo >= tier.min && clubElo <= tier.max) || ELO_TIERS[0];
    const levelDisplay = `${currentTier.name.fr} (${currentTier.level})`;
    const showLevel = stats.eloStarting === 'global';

    const winRateVal = stats.winRate <= 1 ? Math.round(stats.winRate * 100) : Math.round(stats.winRate);
    const podium: any[] = stats.seasonRankings?.slice(0, 3) || [];

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
            {/* Action Bar */}
            <div className="w-full max-w-md mb-6 flex justify-between items-center">
                <button
                    onClick={() => window.location.href = '/'}
                    className="text-slate-400 hover:text-white transition-colors"
                >
                    ← Retour
                </button>
                <button
                    onClick={handleShare}
                    disabled={isSharing}
                    className="bg-amber-400 hover:bg-amber-500 text-black font-bold py-2 px-6 rounded-full transition-colors flex items-center gap-2 shadow-lg shadow-amber-900/20"
                >
                    {isSharing ? 'Génération...' : 'Partager'}
                    {!isSharing && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
                    )}
                </button>
            </div>

            {/* Main Preview Container - Scaled down to fit screen */}
            <div className="relative w-full max-w-[400px] h-[711px] overflow-hidden rounded-xl shadow-2xl border border-slate-700">
                {/* The Recap Card (Exactly 1080x1920 for Story format) */}
                <div
                    ref={recapRef}
                    id="social-share-capture"
                    className="origin-top-left bg-[#0a192f] text-white overflow-hidden relative flex flex-col items-center"
                    style={{
                        width: '1080px',
                        height: '1920px',
                        transform: 'scale(0.37037)', // 400 / 1080 = ~0.37
                        fontFamily: 'Arial, sans-serif'
                    }}
                >
                    {/* Header Image Background */}
                    <div className="relative w-full flex flex-col items-center justify-center text-center overflow-visible mt-20">
                        {/* Banner Image */}
                        <div className="w-full h-[240px] relative flex justify-center items-center mb-8">
                            <img
                                src="/email-assets/bg_card_header.png"
                                alt="Header Background"
                                className="h-full w-auto object-contain scale-150"
                            />
                        </div>

                        {/* Title */}
                        <div className="relative z-10 w-full px-12 mt-4">
                            <h1 className="text-[44px] font-black uppercase tracking-[4px] text-white drop-shadow-lg w-full whitespace-nowrap overflow-hidden text-ellipsis">
                                BILAN DE SAISON - <span className="text-[#fbbf24]">{stats.clubName}</span>
                            </h1>
                            <div className="text-center font-sans text-[22px] text-[#94a3b8] tracking-[2px] uppercase mt-2">
                                PÉRIODE : {formatSeasonPeriod(stats.seasonStart, stats.seasonEnd)}
                            </div>
                        </div>
                    </div>

                    {/* Content Container */}
                    <div className="w-full px-12 pt-8 flex-1 flex flex-col gap-8">

                        {/* 1. Player Ranking */}
                        <div className="relative mt-8">
                            <div className="absolute -top-7 left-0 w-full flex justify-center z-10">
                                <div className="bg-[#0a192f] px-6 text-[#fbbf24] font-bold text-[28px] uppercase tracking-wider flex items-center justify-center min-h-[48px]">
                                    <span style={{ paddingTop: '4px' }}>CLASSEMENT</span>
                                </div>
                            </div>
                            <div className="border-4 border-[#fbbf24] rounded-[20px] bg-[#1e293b]/80 p-0 m-0 pt-12 pb-8">
                                <div className="flex items-center w-full px-2">
                                    {/* Icon */}
                                    <div className="w-1/3 flex justify-center">
                                        <img src="/email-assets/icon_globe_gold.png" alt="Rank" className="w-[104px] h-[104px] object-contain block" />
                                    </div>

                                    {/* Details */}
                                    <div className="w-1/3 flex flex-col justify-center items-center text-center">
                                        <div className="text-[40px] font-bold text-white mb-2 leading-normal whitespace-nowrap pt-1 pb-2">
                                            {shortName(stats.playerName, 15)}
                                        </div>
                                        <div className="text-[28px] font-black text-white leading-normal whitespace-nowrap pb-2">
                                            ELO: <span className="text-[#fbbf24]">{clubElo}</span>
                                        </div>
                                        {showLevel && (
                                            <div className="text-[28px] font-black text-white leading-normal whitespace-nowrap pb-1">
                                                NIVEAU: <span className="text-[#fbbf24]">{levelDisplay}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Position */}
                                    <div className="w-1/3 flex flex-col items-center justify-center">
                                        <div className="text-[72px] font-black text-white leading-none">
                                            #<span className="text-[#fbbf24]">{stats.clubRank || '?'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. Summary Stats */}
                        <div className="relative mt-4">
                            <div className="absolute -top-7 left-0 w-full flex justify-center z-10">
                                <div className="bg-[#0a192f] px-6 text-[#fbbf24] font-bold text-[28px] uppercase tracking-wider flex items-center justify-center min-h-[48px]">
                                    <span style={{ paddingTop: '4px' }}>RÉSULTATS</span>
                                </div>
                            </div>
                            <div className="border-4 border-[#fbbf24] rounded-[20px] bg-[#1e293b]/80 p-0 m-0 pt-10 pb-6">
                                <div className="px-0 flex justify-center w-full gap-0">
                                    {/* Matches */}
                                    <div className="w-1/3 flex flex-col items-center">
                                        <img src="/email-assets/icon_match_gold.png" className="w-[96px] h-[96px] mb-2 block" />
                                        <div className="text-[48px] font-black text-white leading-none">{stats.matches}</div>
                                        <div className="text-[18px] text-[#94a3b8] font-bold uppercase whitespace-nowrap mt-1">MATCHS</div>
                                    </div>
                                    {/* Wins */}
                                    <div className="w-1/3 flex flex-col items-center">
                                        <img src="/email-assets/icon_win_gold.png" className="w-[96px] h-[96px] mb-2 block" />
                                        <div className="text-[48px] font-black text-white leading-none">{stats.wins}</div>
                                        <div className="text-[18px] text-[#94a3b8] font-bold uppercase whitespace-nowrap mt-1">VICTOIRES</div>
                                    </div>
                                    {/* WinRate */}
                                    <div className="w-1/3 flex flex-col items-center">
                                        <img src="/email-assets/icon_trophy_gold.png" className="w-[96px] h-[96px] mb-2 block" />
                                        <div className="text-[48px] font-black text-white leading-none">{winRateVal}%</div>
                                        <div className="text-[18px] text-[#94a3b8] font-bold uppercase whitespace-nowrap mt-1">PERCENT.</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 3. Top Partners */}
                        {stats.topPartners && stats.topPartners.length > 0 && (
                            <div className="relative mt-4">
                                <div className="absolute -top-7 left-0 w-full flex justify-center z-10">
                                    <div className="bg-[#0a192f] px-6 text-[#fbbf24] font-bold text-[28px] uppercase tracking-wider flex items-center justify-center min-h-[48px]">
                                        <span style={{ paddingTop: '4px' }}>PARTENAIRES</span>
                                    </div>
                                </div>
                                <div className="border-4 border-[#fbbf24] rounded-[20px] bg-[#1e293b]/80 p-0 m-0 pt-10 pb-6">
                                    <div className="p-0">
                                        <table className="w-full border-collapse table-fixed">
                                            <tbody>
                                                {stats.topPartners.map((partner: any, i: number) => (
                                                    <tr key={i} className="border-b-2 border-white/10 last:border-0">
                                                        <td className="p-3 pl-6 w-1/3 text-[26px] font-bold text-white align-middle whitespace-nowrap overflow-hidden text-ellipsis text-left">
                                                            <span className="text-[#fbbf24] mr-2">#{i + 1}</span>
                                                            {shortName(partner.partnerName)}
                                                        </td>
                                                        <td className="p-3 w-1/3 text-center text-[26px] font-bold align-middle text-white whitespace-nowrap">
                                                            {partner.winRate}%
                                                        </td>
                                                        <td className="p-3 pr-6 w-1/3 text-center text-[22px] text-[#94a3b8] align-middle whitespace-nowrap">
                                                            {partner.matchesPlayed} M
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {(stats.totalPartners > 3 || (stats.topPartners.length === 3 && stats.totalPartners > 3)) && (
                                            <div className="text-center p-3 pt-2">
                                                <div className="text-[20px] text-[#64748b] italic whitespace-nowrap">
                                                    + {stats.totalPartners - 3} autres partenaires
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 4. Podium */}
                        {podium && podium.length > 0 && (
                            <div className="relative mt-4">
                                <div className="absolute -top-7 left-0 w-full flex justify-center z-10">
                                    <div className="bg-[#0a192f] px-6 text-[#fbbf24] font-bold text-[28px] uppercase tracking-wider flex items-center justify-center min-h-[48px]">
                                        <span style={{ paddingTop: '4px' }}>PODIUM DU CLUB</span>
                                    </div>
                                </div>
                                <div className="border-4 border-[#fbbf24] rounded-[20px] bg-[#1e293b]/80 p-0 m-0 pt-12 pb-8">
                                    <div className="flex justify-center items-end h-[240px] px-4 gap-6">
                                        {/* 2nd Place */}
                                        <div className="w-[30%] flex flex-col items-center justify-end h-full">
                                            {podium[1] && (
                                                <>
                                                    <span className="text-[26px] font-bold text-white mb-2 truncate max-w-full text-center">{shortName(podium[1].name || podium[1].playerName)}</span>
                                                    <span className="text-[24px] text-slate-400 mb-4">{podium[1].clubEloRating} ELO</span>
                                                    <div className="w-full bg-gradient-to-t from-slate-600 to-slate-400 rounded-t-lg h-[110px] flex items-center justify-center shadow-lg">
                                                        <span className="text-white font-black text-[56px] drop-shadow-md">2</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* 1st Place */}
                                        <div className="w-[35%] flex flex-col items-center justify-end h-full">
                                            {podium[0] && (
                                                <>
                                                    <span className="text-[32px] font-bold text-white mb-2 truncate max-w-full text-center">{shortName(podium[0].name || podium[0].playerName)}</span>
                                                    <span className="text-[28px] text-amber-400 mb-3">{podium[0].clubEloRating} ELO</span>
                                                    <div className="w-full bg-gradient-to-t from-amber-700 to-amber-400 rounded-t-lg h-[155px] flex items-center justify-center border-4 border-amber-400 shadow-[0_0_20px_rgba(251,191,36,0.5)] z-10">
                                                        <span className="text-white font-black text-[72px] drop-shadow-lg">1</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* 3rd Place */}
                                        <div className="w-[30%] flex flex-col items-center justify-end h-full">
                                            {podium[2] && (
                                                <>
                                                    <span className="text-[26px] font-bold text-white mb-2 truncate max-w-full text-center">{shortName(podium[2].name || podium[2].playerName)}</span>
                                                    <span className="text-[24px] text-orange-500 mb-4">{podium[2].clubEloRating} ELO</span>
                                                    <div className="w-full bg-gradient-to-t from-orange-800 to-orange-500 rounded-t-lg h-[80px] flex items-center justify-center shadow-lg">
                                                        <span className="text-white font-black text-[48px] drop-shadow-md">3</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>

                    {/* 5. Footer inside Image */}
                    <div className="flex flex-col items-center justify-center mt-auto mb-8 w-full">
                        <div className="text-center w-full">
                            <div className="text-[#fbbf24] font-black text-[40px] uppercase tracking-widest drop-shadow-md">www.padelo.fr</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-4 text-slate-500 text-xs text-center max-w-sm">
                Astuce: Si le partage ne fonctionne pas, faites une capture d'écran !
            </div>
        </div>
    );
}
